import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { DfModuleImageModule } from '@df/module-image/df-module-image.module';
import { ImageModule } from '@df/module-image/image.module';
import { LayoutWidthOptions, RatioOptions, UndefinedOption } from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { VimeoPlayerComponent } from '@df/ui/display/media/vimeo-player.component';
import { YoutubePlayerComponent } from '@df/ui/display/media/youtube-player.component';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { NTextOverlay, TextOverlayComponent } from 'projects/app-default/src/app/common/text-overlay/text-overlay.component';
import { defaultImagePng } from 'projects/app-default/src/app/icms.config';
import { NShopThisLook, ShopThisLookomponent } from 'projects/app-default/src/app/ui/shop-this-look/shop-this-look.component';
import { IPageComponentHeroImageBlockData, IPageComponentHeroImageData } from './hero-image.interface';
import { App } from '@df/core/app';

let uid = 0;

@Component({
    selector: 'page-component-hero-image',
    templateUrl: './hero-image.html',
    styleUrls: ['hero-image.scss'],
    standalone: true,
    imports: [
        DfModuleImageModule,
        HrefComponent,
        ICmsClientModule,
        ImageModule,
        NgClass,
        ShopThisLookomponent,
        TextOverlayComponent,
        VimeoPlayerComponent,
        YoutubePlayerComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<PageComponentHeroImageComponent>({
    id: 'hero-image',
    name: 'Hero Image',
    description: 'Wide banner with text lines and optional link',
    tags: ['banner', 'link', 'big'],
    fields: [
        // general
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-general',
            title: 'General'
        },
        {
            key: 'link',
            type: NICmsContentField.EFieldType.text,
            title: 'Link',
            description:
                'Please use path like /some-path for internal usage or absolute url https://example.com/some-page for external reference'
        },
        // background
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-background',
            title: 'Background'
        },
        <NICmsContentField.IChoiceField>{
            key: 'ratio',
            type: NICmsContentField.EFieldType.choice,
            title: 'Ratio',
            options: RatioOptions,
            description: 'Proportions width x height',
            default: 'ratio-2-1',
            required: true
        },
        {
            key: 'background_image',
            type: NICmsContentField.EFieldType.image,
            title: 'Image',
            default: defaultImagePng
        },
        // video
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-positioning',
            title: 'Video'
        },
        {
            key: 'video_id',
            type: NICmsContentField.EFieldType.text,
            title: 'ID or URL',
            description: 'Video ID or URL'
        },
        <NICmsContentField.IChoiceField>{
            key: 'video_provider',
            type: NICmsContentField.EFieldType.choice,
            title: 'Provider',
            options: [
                ...UndefinedOption,
                {
                    key: 'youtube',
                    title: 'YouTube'
                },
                {
                    key: 'vimeo',
                    title: 'Vimeo'
                }
            ],
            description: 'Choose a video provider'
        },
        <NICmsContentField.IToggleField>{
            key: 'video_autoplay',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Autoplay',
            default: true
        },
        <NICmsContentField.IToggleField>{
            key: 'video_background',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Background Mode',
            default: true
        },
        <NICmsContentField.IToggleField>{
            key: 'video_hide',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Hide',
            default: false
        },
        <NICmsContentField.IToggleField>{
            key: 'button_hide',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Hide Play/Pause Button',
            default: false
        },
        // content
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-content',
            title: 'Content'
        },
        <NICmsContentField.IChoiceField>{
            type: NICmsContentField.EFieldType.choice,
            title: 'Content Width',
            key: 'width',
            itemName: 'width',
            options: LayoutWidthOptions,
            default: undefined
        },
        // Text blocks
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'blocks',
            title: 'Content Blocks',
            itemName: 'Block',
            itemNameField: 'block',
            defaultSize: 1,
            complex: true,
            fields: [
                // general
                {
                    type: NICmsContentField.EFieldType.label,
                    key: 'blocks-general',
                    title: 'General'
                },
                {
                    key: 'custom',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Use Custom Components',
                    default: false
                },
                <NICmsContentField.IComponentsField>{
                    key: 'components',
                    type: NICmsContentField.EFieldType.components,
                    title: 'Components',
                    itemName: 'component',
                    default: [],
                    canShow: (data: IPageComponentHeroImageBlockData) => data?.custom
                },
                {
                    key: 'style',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Custom Styling',
                    description: 'Direct CSS. Please be careful when changing this.',
                    canShow: (data: IPageComponentHeroImageBlockData) => !data?.custom
                },
                // text posistioning
                {
                    type: NICmsContentField.EFieldType.label,
                    key: 'blocks-positioning',
                    title: 'Positioning'
                },
                ...NTextOverlay.getFields()
            ],
            default: [
                {
                    custom: false,
                    components: [],
                    left_position: 37,
                    top_position: 58,
                    lines: [
                        {
                            type: 'button',
                            align: 'center',
                            button_class: 'button-1',
                            text: 'Shop the Collection',
                            button_link: '/'
                        }
                    ],
                    'left_position@S': 0,
                    'top_position@S': 75
                }
            ]
        },
        ...NShopThisLook.fields
    ]
})
export class PageComponentHeroImageComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: IPageComponentHeroImageData;

    protected readonly isPlatformBrowser = inject(App).isPlatformBrowser;

    protected buttonShadow(buttonClass: string): string {
        if (buttonClass.match('button-inline')) {
            return '_underline-shadow _text-shadow';
        } else {
            return '_button-box-shadow';
        }
    }

    protected uid = uid++;
}
